import store from '@/store';

export default class AddedTagInAttendanceEvent {
  constructor(body) {
    try {
      const protocol =
        String(
          body?.protocol ??
            body?.attendance?.protocol ??
            body?.information?.protocol ??
            '',
        ) || '';
      if (!protocol) return;

      const toArray = (v) => (Array.isArray(v) ? v : []);
      const flattenItems = (arr) => {
        const first = arr?.[0];
        if (first && typeof first === 'object' && Array.isArray(first.items)) {
          return arr.flatMap((g) => g.items || []);
        }
        return arr || [];
      };
      const normalize = (t) => ({
        id: String(t?.tag_item_id ?? t?.id ?? t?.value ?? t?.tag?.id ?? ''),
        name: String(
          t?.tag_item_name ?? t?.name ?? t?.label ?? t?.tag?.name ?? '',
        ),
        color: String(
          t?.tag_item_color ?? t?.color ?? t?.hex_color ?? t?.tag?.color ?? '',
        ),
      });
      const normalizeGroup = (g) => ({
        id: String(g?.id ?? g?.tag_id ?? ''),
        name: String(g?.name ?? g?.tag_name ?? ''),
        selection: String(g?.selection ?? g?.tag_selection ?? ''),
        required: Boolean(g?.required ?? g?.tag_required ?? false),
        editable: Boolean(g?.editable ?? g?.tag_editable ?? true),
        helper_text: String(g?.helper_text ?? g?.tag_helper_text ?? ''),
        description: String(g?.description ?? g?.tag_description ?? ''),
        items: toArray(g?.items).map(normalize),
      });

      const rawTagField = body?.tag;
      const rawGroups = Array.isArray(rawTagField)
        ? rawTagField
        : rawTagField
          ? [rawTagField]
          : undefined;

      const rawTags =
        rawGroups ??
        body?.tags ??
        body?.attendanceTags ??
        body?.attendance_tags ??
        body?.selectedTags ??
        body?.tagList ??
        body?.tagsSelected ??
        (body?.attendance?.tags || []);

      const tags = Array.from(
        new Map(
          flattenItems(toArray(rawTags))
            .map(normalize)
            .map((t) => [t.id, t]),
        ).values(),
      ).sort((a, b) => a.name.localeCompare(b.name));

      const isGroupPayload =
        Array.isArray(rawTags) &&
        rawTags.some(
          (g) => g && typeof g === 'object' && Array.isArray(g.items),
        );

      const getCurrentFlattenedFromStore = () => {
        try {
          const job =
            store.getters &&
            store.getters['jobs/getJobByProtocol'] &&
            store.getters['jobs/getJobByProtocol'](protocol);
          const groups = (job && job.attendanceTags) || [];
          const items = (groups || []).flatMap((g) => toArray(g.items));
          return Array.from(
            new Map(items.map(normalize).map((t) => [t.id, t])).values(),
          ).sort((a, b) => a.name.localeCompare(b.name));
        } catch {
          return [];
        }
      };

      const previousFlattened = getCurrentFlattenedFromStore();

      let dispatchTags = tags;

      if (isGroupPayload) {
        const incomingGroups = toArray(rawTags).map(normalizeGroup);
        const getGroupsFromStore = () => {
          try {
            const job =
              store.getters &&
              store.getters['jobs/getJobByProtocol'] &&
              store.getters['jobs/getJobByProtocol'](protocol);
            return job && Array.isArray(job.attendanceTags)
              ? job.attendanceTags
              : [];
          } catch {
            return [];
          }
        };
        const currentGroups = getGroupsFromStore();
        const byId = new Map();
        (currentGroups || []).forEach(function (g) {
          byId.set(String(g && g.id), {
            id: String(g && g.id),
            name: String((g && g.name) || ''),
            selection: String((g && g.selection) || ''),
            required: !!(g && g.required),
            editable: !!(g && g.editable),
            helper_text: String((g && g.helper_text) || ''),
            description: String((g && g.description) || ''),
            items: toArray(g && g.items).map(normalize),
          });
        });
        incomingGroups.forEach(function (g) {
          const gid = String(g && g.id);
          const existing = byId.get(gid) || {
            id: gid,
            name: String((g && g.name) || ''),
            selection: String((g && g.selection) || ''),
            required: !!(g && g.required),
            editable: !!(g && g.editable),
            helper_text: String((g && g.helper_text) || ''),
            description: String((g && g.description) || ''),
            items: [],
          };
          const unionMap = new Map();
          toArray(existing.items).forEach(function (it) {
            unionMap.set(String(it && it.id), normalize(it));
          });
          toArray(g && g.items).forEach(function (it) {
            unionMap.set(
              String((it && it.id) || (it && it.tag_item_id)),
              normalize(it),
            );
          });
          existing.items = Array.from(unionMap.values()).sort(function (a, b) {
            return String(a.name).localeCompare(String(b.name));
          });
          existing.name = String((g && g.name) || existing.name || '');
          existing.selection = String(
            (g && g.selection) || existing.selection || '',
          );
          existing.required = !!((g && g.required) || existing.required);
          existing.editable = !!((g && g.editable) || existing.editable);
          existing.helper_text = String(
            (g && g.helper_text) || existing.helper_text || '',
          );
          existing.description = String(
            (g && g.description) || existing.description || '',
          );
          byId.set(gid, existing);
        });
        const updatedGroups = Array.from(byId.values());
        store.commit(
          'jobs/SET_ATTENDANCE_TAGS_JOB',
          { protocol, attendanceTags: updatedGroups, merge: false },
          { root: true },
        );
        dispatchTags = getCurrentFlattenedFromStore();
      } else {
        const current = getCurrentFlattenedFromStore();
        const merged = new Map(current.map((t) => [String(t.id), t]));
        tags.forEach((t) => merged.set(String(t.id), t));
        dispatchTags = Array.from(merged.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );
      }
      const previousIdSet = new Set(
        Array.isArray(previousFlattened)
          ? previousFlattened.map((t) => String(t && t.id))
          : [],
      );
      const addedTags = Array.isArray(dispatchTags)
        ? dispatchTags.filter((t) => !previousIdSet.has(String(t && t.id)))
        : [];

      window.dispatchEvent(
        new CustomEvent('attendance:tags:updated', {
          detail: { protocol, tags: dispatchTags, addedTags },
        }),
      );
    } catch (error) {}
  }
}
